import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { CTA, findTourForRoute, getTourByKey, TOURS } from './tours'
import { waitForAnchor } from './anchors'
import {
  hasSeenTour,
  isSkipAll,
  markTourSeen,
  mergeRemoteProgress,
  resetTours,
  setSkipAll
} from './storage'
import {
  fetchTourProgress,
  markTourProgressRemote
} from 'services/toursService'
import TourSpotlight from './TourSpotlight'

const TourContext = createContext({
  startTour: () => {},
  endTour: () => {},
  activeTourKey: null
})

/*
 * Routes here mount, fetch, then re-render. Pointing at a loading skeleton is
 * worse than being a second late.
 */
const AUTO_START_DELAY = 1200
const BLOCKED_RETRY_DELAY = 1500

/*
 * Never interrupt something the user already has open. What's-new pops on
 * login, the tutorial player takes over the screen, and any MUI dialog owns
 * the user's attention — a tour arriving on top of one reads as a bug.
 */
const isScreenBusy = () =>
  Boolean(document.querySelector('.MuiDialog-root')) ||
  document.documentElement.classList.contains('bp-tutorial-open') ||
  document.documentElement.classList.contains('bp-workflow-running')

/*
 * Module-level on purpose: survives the provider re-rendering, resets on a
 * full page load. One attempt per tour per page load — and nothing stricter.
 * An earlier "one tour per session" rule hid the create-passport tour from
 * anyone who had just seen the units one; two screens are two contexts, and
 * a user walking from one to the other should get both.
 */
const attemptedThisSession = new Set()

export const TourProvider = ({ children }) => {
  const location = useLocation()
  const currentUser = useSelector(getCurrentUser)
  const userId = currentUser?.id || null

  const [active, setActive] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [progressLoaded, setProgressLoaded] = useState(false)

  const activeRef = useRef(null)
  const resolvedAnyRef = useRef(false)
  const userIdRef = useRef(userId)

  useEffect(() => {
    activeRef.current = active
  }, [active])

  useEffect(() => {
    userIdRef.current = userId
  }, [userId])

  /*
   * Pull what this user has already been through on another device. Silent on
   * failure and non-blocking: the local record is already usable, and the only
   * cost of not getting this is a tour the user has seen before showing once
   * more.
   */
  useEffect(() => {
    if (!userId) return undefined
    let live = true
    fetchTourProgress()
      .then(res => {
        if (live) {
          mergeRemoteProgress(userId, res?.data?.data)
          setProgressLoaded(true)
        }
      })
      .catch(() => {
        if (live) setProgressLoaded(true)
      })
    return () => {
      live = false
    }
  }, [userId])

  const finish = useCallback(status => {
    const current = activeRef.current
    setActive(null)
    setAnchorEl(null)
    if (!current) return
    /*
     * Only record a tour the user could actually see. If every step was
     * skipped — wrong role, feature not in their plan, nothing to reconcile —
     * marking it seen would burn the tour for the day they do get access.
     */
    if (resolvedAnyRef.current) {
      markTourSeen(userIdRef.current, current.tour.key, status, current.tour.version)
      // Local first so the tour cannot flash back on the next navigation; the
      // server copy is what makes it stick across devices.
      markTourProgressRemote(
        current.tour.key,
        status,
        current.tour.version
      ).catch(() => {})
    }
    resolvedAnyRef.current = false
  }, [])

  const startTour = useCallback(key => {
    const tour = getTourByKey(key)
    if (!tour || !tour.steps?.length) return
    resolvedAnyRef.current = false
    setAnchorEl(null)
    setActive({ tour, stepIndex: 0 })
  }, [])

  const advance = useCallback(() => {
    setAnchorEl(null)
    setActive(prev =>
      prev ? { tour: prev.tour, stepIndex: prev.stepIndex + 1 } : null
    )
  }, [])

  // Resolve the current step's anchor, or skip past it.
  useEffect(() => {
    if (!active) return undefined

    const step = active.tour.steps[active.stepIndex]
    if (!step) {
      finish('completed')
      return undefined
    }

    let cancelled = false
    // A step that clicks the control for the user needs it actually pressable;
    // one that only points at it does not care.
    const requireEnabled =
      step.requireEnabled ?? step.cta?.type === CTA.clickAnchor

    waitForAnchor(step.anchor, step.waitMs, requireEnabled).then(el => {
      if (cancelled) return
      if (el) {
        resolvedAnyRef.current = true
        setAnchorEl(el)
      } else {
        advance()
      }
    })

    return () => {
      cancelled = true
    }
  }, [active, advance, finish])

  /*
   * Auto-start whatever this route has pending.
   *
   * Waits for the tour's first anchor for as long as the user stays on the
   * route, not for a fixed number of seconds. On create-passport the control
   * being explained only exists after the user has searched a postcode and
   * picked a building — a timer would have given up long before. Everything
   * here is torn down on route change, so nothing keeps watching a page the
   * user has left.
   */
  useEffect(() => {
    if (!userId || activeRef.current) return undefined
    // Without this a tour finished on another device shows one extra time
    // before the merged record lands.
    if (!progressLoaded) return undefined
    if (isSkipAll(userId)) return undefined

    const tour = findTourForRoute(location.pathname)
    if (!tour) return undefined
    if (attemptedThisSession.has(tour.key)) return undefined
    if (hasSeenTour(userId, tour.key, tour.version)) return undefined

    const firstStep = tour.steps[0]
    const requireEnabled =
      firstStep.requireEnabled ?? firstStep.cta?.type === CTA.clickAnchor

    let cancelled = false
    let timer = null
    const watch = waitForAnchor(firstStep.anchor, null, requireEnabled)

    const attempt = () => {
      if (cancelled || activeRef.current) return
      if (isScreenBusy()) {
        // Wait it out rather than dropping the tour: the user is usually a
        // click away from dismissing whatever is in front of it, and this is
        // torn down the moment they leave the route.
        timer = setTimeout(attempt, BLOCKED_RETRY_DELAY)
        return
      }
      attemptedThisSession.add(tour.key)
      startTour(tour.key)
    }

    watch.then(el => {
      if (cancelled || !el) return
      timer = setTimeout(attempt, AUTO_START_DELAY)
    })

    return () => {
      cancelled = true
      watch.cancel()
      if (timer) clearTimeout(timer)
    }
  }, [location.pathname, userId, progressLoaded, startTour])

  // Same escape hatch the tutorials and what's-new panels already use, so a
  // help menu can replay a tour without importing this module.
  useEffect(() => {
    const onStart = event => {
      const key = event?.detail?.key
      if (key) startTour(key)
    }
    window.addEventListener('bp:start-tour', onStart)
    return () => window.removeEventListener('bp:start-tour', onStart)
  }, [startTour])

  useEffect(() => {
    window.__bpTours = {
      list: () => TOURS.map(t => t.key),
      start: key => startTour(key),
      reset: () => resetTours(userIdRef.current),
      skipAll: (value = true) => setSkipAll(userIdRef.current, value)
    }
    return () => {
      delete window.__bpTours
    }
  }, [startTour])

  const step = active ? active.tour.steps[active.stepIndex] : null

  const handleCta = useCallback(() => {
    const current = activeRef.current
    const currentStep = current
      ? current.tour.steps[current.stepIndex]
      : null
    if (!currentStep) return

    if (currentStep.cta?.type === CTA.dismiss) {
      finish('dismissed')
      return
    }
    if (currentStep.cta?.type === CTA.clickAnchor && anchorEl) {
      // The next step waits for whatever this opens.
      anchorEl.click()
    }
    advance()
  }, [advance, anchorEl, finish])

  const handleDismiss = useCallback(() => finish('dismissed'), [finish])

  const value = useMemo(
    () => ({
      startTour,
      endTour: handleDismiss,
      activeTourKey: active?.tour.key || null
    }),
    [startTour, handleDismiss, active]
  )

  return (
    <TourContext.Provider value={value}>
      {children}
      {step && anchorEl && (
        <TourSpotlight
          step={step}
          anchorEl={anchorEl}
          stepNumber={active.stepIndex + 1}
          stepCount={active.tour.steps.length}
          onNext={advance}
          onDismiss={handleDismiss}
          onCtaClick={handleCta}
        />
      )}
    </TourContext.Provider>
  )
}

export const useTour = () => useContext(TourContext)

export default TourProvider
